import { type FC } from 'react'
import { MiteneFormattedMessage } from 'components/atoms/MiteneFormattedMessage'
import { MiteneCrownIcon } from 'components/design-system/primitives/Icons/MiteneCrownIcon'
import { PlanCard } from 'components/design-system/primitives/PlanCard'
import { Text } from 'components/design-system/primitives/Typography'

export type PlanPeriod = 'month' | 'year'
export type PlanType = 'premium' | 'premiumPro'

export interface PlanProps {
  name: string
  price: string
  priceId: string
  interval: string
  appProductId: string
  discountedMonthlyPrice?: string
}

export interface PlanSelectionProps {
  onSelectPlan: (priceId: string) => void
  selectedPriceId: string
  premiumPlan: PlanProps[]
  proPlan: PlanProps[]
  currentPlan?: string
}

export const PlanSelection: FC<PlanSelectionProps> = ({
  onSelectPlan,
  selectedPriceId,
  premiumPlan,
  proPlan,
  currentPlan = null,
}) => {
  // プラン情報の取得 - intervalを使用
  const yearlyProPlan = proPlan.find(plan => plan.interval === 'year')
  const monthlyProPlan = proPlan.find(plan => plan.interval === 'month')
  const yearlyPremiumPlan = premiumPlan.find(plan => plan.interval === 'year')
  const monthlyPremiumPlan = premiumPlan.find(plan => plan.interval === 'month')

  // 月額換算の価格はサーバー側で計算済み
  const proMonthlyEquivalent = yearlyProPlan?.discountedMonthlyPrice
  const premiumMonthlyEquivalent = yearlyPremiumPlan?.discountedMonthlyPrice

  // プラン選択時の共通ハンドラ（priceIdを引数で受け取る）
  const handleSelectPlan = (priceId: string) => {
    // 選択されたプランIDを親に通知
    onSelectPlan(priceId)
  }

  return (
    <div className='flex flex-col gap-6'>
      {/* プレミアムプラン */}
      <div className='flex flex-col'>
        <div className='flex flex-col gap-1 mb-2'>
          <div className='flex items-center justify-left gap-1'>
            <MiteneCrownIcon variant='premium' size={20} />
            <Text bold={true}>
              <MiteneFormattedMessage id='planSelection.premium.title' />
            </Text>
          </div>
          <Text size='small'>
            <MiteneFormattedMessage id='planSelection.premium.description' />
          </Text>
        </div>

        <div className='flex gap-2'>
          {yearlyPremiumPlan && (
            <PlanCard
              price={yearlyPremiumPlan.price}
              period='year'
              discountedMonthlyPrice={premiumMonthlyEquivalent}
              checked={selectedPriceId === yearlyPremiumPlan.priceId}
              priceId={yearlyPremiumPlan.priceId}
              onSelect={handleSelectPlan}
              subscribed={currentPlan === yearlyPremiumPlan.appProductId}
            />
          )}
          {monthlyPremiumPlan && (
            <PlanCard
              price={monthlyPremiumPlan.price}
              period='month'
              checked={selectedPriceId === monthlyPremiumPlan.priceId}
              priceId={monthlyPremiumPlan.priceId}
              onSelect={handleSelectPlan}
              subscribed={currentPlan === monthlyPremiumPlan.appProductId}
            />
          )}
        </div>
      </div>

      {/* プレミアムProプラン */}
      <div className='flex flex-col'>
        <div className='flex flex-col gap-1 mb-2'>
          <div className='flex items-center justify-left gap-1'>
            <MiteneCrownIcon variant='pro' size={20} />
            <Text bold={true}>
              <MiteneFormattedMessage id='planSelection.premiumPro.title' />
            </Text>
          </div>
          <Text size='small'>
            <MiteneFormattedMessage id='planSelection.premiumPro.description' />
          </Text>
        </div>

        <div className='flex gap-2'>
          {yearlyProPlan && (
            <PlanCard
              price={yearlyProPlan.price}
              period='year'
              discountedMonthlyPrice={proMonthlyEquivalent}
              checked={selectedPriceId === yearlyProPlan.priceId}
              priceId={yearlyProPlan.priceId}
              onSelect={handleSelectPlan}
              subscribed={currentPlan === yearlyProPlan.appProductId}
            />
          )}
          {monthlyProPlan && (
            <PlanCard
              price={monthlyProPlan.price}
              period='month'
              checked={selectedPriceId === monthlyProPlan.priceId}
              priceId={monthlyProPlan.priceId}
              onSelect={handleSelectPlan}
              subscribed={currentPlan === monthlyProPlan.appProductId}
            />
          )}
        </div>
      </div>
    </div>
  )
}
